
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Loader2, Check, Info, AlertCircle, ImageIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { generateBlogCoverImage } from '@/utils/blogGenerator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

interface WizardImageSelectorProps {
  imageOptions: Array<{
    url: string;
    attribution?: {
      photographer: string;
      photographerUrl?: string;
    };
  }>;
  topic: string;
  imageSource: 'huggingface' | 'pexels';
  onSelectImage: (url: string, attribution?: { photographer: string; photographerUrl?: string }) => void;
  onGenerateMore: () => void;
}

export const WizardImageSelector: React.FC<WizardImageSelectorProps> = ({
  imageOptions,
  topic,
  imageSource,
  onSelectImage,
  onGenerateMore,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isGeneratingCustom, setIsGeneratingCustom] = useState(false);
  const [currentImageSource, setCurrentImageSource] = useState<'huggingface' | 'pexels'>(imageSource);
  const { toast } = useToast();

  const handleGenerateCustomImage = async () => {
    if (!customPrompt.trim()) {
      toast({
        title: "Prompt Required",
        description: "Please enter a description for the image you want",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingCustom(true);

    try {
      console.log(`Attempting to generate custom image with ${currentImageSource} for prompt: "${customPrompt}"`);
      toast({
        title: "Generating Image",
        description: `Creating image with ${currentImageSource === 'huggingface' ? 'AI generation' : 'stock photos'}...`,
      });
      
      const imageResult = await generateBlogCoverImage(customPrompt, currentImageSource);
      
      // Add the new image to options
      const newImage = {
        url: imageResult.imageUrl,
        attribution: {
          photographer: imageResult.photographer || "AI Generated",
          photographerUrl: imageResult.photographerUrl
        }
      };
      
      onSelectImage(newImage.url, newImage.attribution);
      
      toast({
        title: "Image Generated",
        description: "Custom image has been generated and selected",
      });

    } catch (error) {
      console.error("Error generating custom image:", error);
      
      // Try with the other source if this one failed
      const alternateSource = currentImageSource === 'huggingface' ? 'pexels' : 'huggingface';
      
      toast({
        title: "Trying Alternative Source",
        description: `Image generation with ${currentImageSource} failed. Trying with ${alternateSource}...`,
      });
      
      try {
        console.log(`Attempting fallback with ${alternateSource} for prompt: "${customPrompt}"`);
        const imageResult = await generateBlogCoverImage(customPrompt, alternateSource);
        
        // Add the new image to options
        const newImage = {
          url: imageResult.imageUrl,
          attribution: {
            photographer: imageResult.photographer || "AI Generated",
            photographerUrl: imageResult.photographerUrl
          }
        };
        
        onSelectImage(newImage.url, newImage.attribution);
        
        toast({
          title: "Image Generated",
          description: `Successfully generated image using alternative source (${alternateSource})`,
        });
        
        // Update the current image source to the one that worked
        setCurrentImageSource(alternateSource);
        
      } catch (fallbackError) {
        console.error("Fallback image generation also failed:", fallbackError);
        toast({
          title: "Image Generation Failed",
          description: "Both image sources failed. Please try a different prompt or contact support.",
          variant: "destructive",
        });
      }
    } finally {
      setIsGeneratingCustom(false);
    }
  };

  const handleGenerateMore = async () => {
    setIsGeneratingMore(true);
    try {
      await onGenerateMore();
    } catch (error) {
      console.error("Error generating more images:", error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate additional images. Please try again or use a different prompt.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingMore(false);
    }
  };

  const handleSourceChange = (value: 'huggingface' | 'pexels') => {
    setCurrentImageSource(value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Select a cover image</h3>
        <p className="text-sm text-gray-500 mb-4">
          Choose one of the generated images or create a custom one with a specific prompt
        </p>
      </div>

      {imageOptions.length === 0 ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription>
            No images could be generated. Try generating images again with a different prompt or image source.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {imageOptions.map((image, index) => (
            <div
              key={index}
              onClick={() => setSelectedImageIndex(index)}
              className={`relative aspect-video rounded-md overflow-hidden border-2 cursor-pointer transition-all ${
                selectedImageIndex === index ? 'border-edgefi-purple ring-2 ring-edgefi-purple/20' : 'border-transparent hover:border-gray-200'
              }`}
            >
              <img 
                src={image.url} 
                alt={`Option ${index + 1}`} 
                className="object-cover w-full h-full" 
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/600x400?text=Image+Error';
                }}
              />
              {image.attribution && (
                <div className="absolute bottom-0 right-0 bg-black/50 text-white text-xs px-2 py-1">
                  Photo by {image.attribution.photographer}
                </div>
              )}
              {selectedImageIndex === index && (
                <div className="absolute top-2 right-2 bg-edgefi-purple text-white rounded-full p-1">
                  <Check className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center">
        <Button
          onClick={handleGenerateMore}
          variant="outline"
          disabled={isGeneratingMore}
        >
          {isGeneratingMore ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Generate More Options
            </>
          )}
        </Button>

        <Button
          onClick={() => {
            if (selectedImageIndex !== null) {
              const selected = imageOptions[selectedImageIndex];
              onSelectImage(selected.url, selected.attribution);
            }
          }}
          className="bg-edgefi-purple hover:bg-purple-700"
          disabled={selectedImageIndex === null || imageOptions.length === 0}
        >
          Use Selected Image
        </Button>
      </div>

      <div className="mt-8 border-t pt-6">
        <h3 className="text-md font-medium mb-2">Generate Custom Image</h3>
        <p className="text-sm text-gray-500 mb-4">
          Describe exactly what image you want to create
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Image Source</p>
            <RadioGroup 
              value={currentImageSource} 
              onValueChange={(value) => handleSourceChange(value as 'huggingface' | 'pexels')}
              className="flex space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pexels" id="pexels" />
                <Label htmlFor="pexels">Stock Photos</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="huggingface" id="huggingface" />
                <Label htmlFor="huggingface">AI Generated</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder={`e.g., "Professional IT team securing a network" or "${topic}"`}
              className="flex-1 px-3 py-2 border rounded-md"
              disabled={isGeneratingCustom}
            />
            <Button
              onClick={handleGenerateCustomImage}
              disabled={!customPrompt.trim() || isGeneratingCustom}
              className="bg-edgefi-purple hover:bg-purple-700"
            >
              {isGeneratingCustom ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
